/**
 * Owns the socket for one game of hartenjagen.
 *
 * Mirrors `useLiveSession` and shares `GameSocket` and the stored-session
 * helpers with it. What differs is what goes over the socket — three cards at
 * once instead of a bid — and which store the messages land in.
 */

import { useCallback, useEffect } from 'react';
import type { Card } from '@shared/types/game';
import type { ClientMessage } from '@shared/types/websocket';
import { GameSocket } from '@/live/socket';
import { rememberSeat, seatFor } from '@/live/sessions';
import { useHearts } from '@/store/hearts';
import { useAuth } from '@/store/auth';

export interface HeartsActions {
  ready: (ready: boolean) => void;
  start: () => void;
  /** Seat a computer player, or drop the last one. Host only, lobby only. */
  addBot: () => void;
  removeBot: () => void;
  pass: (cards: Card[]) => void;
  play: (card: Card) => void;
  leave: () => void;
}

export function useHeartsSession(
  code: string | undefined,
  name: string | null,
  profileId: string | null = null
): HeartsActions {
  const handle = useHearts((state) => state.handle);
  const setStatus = useHearts((state) => state.setStatus);
  const reset = useHearts((state) => state.reset);

  useEffect(() => {
    if (!code) return;
    const upper = code.toUpperCase();

    const socket = new GameSocket(
      (message) => {
        if (message.type === 'joined') {
          rememberSeat({
            code: upper,
            mode: 'hearts',
            rejoinToken: message.rejoinToken,
            name: message.state.players[message.seat]?.name ?? name ?? '',
          });
        }
        handle(message);
      },
      setStatus,
      // Recomputed on every (re)connect, so a token obtained during this
      // session is used by the reconnect that follows it.
      (): ClientMessage | null => {
        const stored = seatFor(upper);
        if (stored) {
          return {
            type: 'join',
            code: upper,
            playerName: stored.name,
            rejoinToken: stored.rejoinToken,
          };
        }
        if (!name) return null;
        return { type: 'join', code: upper, playerName: name, profileId };
      },
      undefined,
      // A refused handshake and a restarting server look identical to a
      // browser, so ask the side that can tell the difference.
      () => void useAuth.getState().refresh()
    );

    socket.connect();
    heldSocket = socket;

    return () => {
      socket.close();
      heldSocket = null;
      reset();
    };
  }, [code, name, profileId, handle, setStatus, reset]);

  const send = useCallback((message: ClientMessage) => heldSocket?.send(message), []);

  return {
    ready: useCallback((ready: boolean) => send({ type: 'ready', ready }), [send]),
    start: useCallback(() => send({ type: 'start-game' }), [send]),
    addBot: useCallback(() => send({ type: 'add-bot' }), [send]),
    removeBot: useCallback(() => send({ type: 'remove-bot' }), [send]),
    pass: useCallback((cards: Card[]) => send({ type: 'pass-cards', cards }), [send]),
    play: useCallback((card: Card) => send({ type: 'play-card', card }), [send]),
    leave: useCallback(() => heldSocket?.close(), []),
  };
}

/*
 * Module-level rather than a ref, so the action callbacks can stay stable
 * across renders without threading the socket through every component. Only
 * one hearts table can be open in a tab, which is what makes that safe.
 */
let heldSocket: GameSocket | null = null;
